import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  CheckCircle,
  Shield,
  Users,
  Headphones
} from "lucide-react";
import { toast } from "sonner";

// Contact info (static data)
const contactInfo = {
  phone: "+49 (0) 123 456 7890",
  email: "info@netsyte.de",
  address: {
    street: "Musterstraße 123",
    city: "12345 Musterstadt",
    country: "Deutschland"
  },
  hours: {
    weekdays: "Mo-Fr: 8:00 - 18:00 Uhr",
    weekend: "Sa: 9:00 - 12:00 Uhr", 
    emergency: "IT-Notfall-Support"
  }
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      service: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { contactAPI } = await import('../services/api');
      
      const inquiryData = {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        phone: formData.phone || null,
        service: formData.service || null,
        message: formData.message,
        inquiry_type: "general"
      };
      
      await contactAPI.createInquiry(inquiryData);
      
      toast.success("Nachricht erfolgreich gesendet! Wir melden uns innerhalb von 24 Stunden bei Ihnen.");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        service: "",
        message: ""
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error("Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    "IT-Infrastruktur",
    "IT-Sicherheit", 
    "Netzwerkmanagement",
    "IT-Beratung",
    "CyberRisikoCheck",
    "Service-Flatrate",
    "Service on Demand",
    "Serverwartung",
    "Cloud-Telefonie",
    "Sonstiges"
  ];

  const contactMethods = [
    {
      icon: Phone,
      title: "Telefon",
      value: contactInfo.phone,
      description: "Mo-Fr: 8:00-18:00 Uhr",
      color: "blue"
    },
    {
      icon: Mail,
      title: "E-Mail",
      value: contactInfo.email,
      description: "Antwort binnen 24h",
      color: "green"
    },
    {
      icon: Headphones,
      title: "IT-Notfall-Support",
      value: "+49 (0) 123 456 7899",
      description: "Für bestehende Kunden",
      color: "red"
    }
  ];

  const reasons = [
    {
      icon: CheckCircle,
      title: "Schnelle Reaktionszeit",
      description: "Antwort innerhalb von 24 Stunden"
    },
    {
      icon: Shield,
      title: "Kostenlose Erstberatung",
      description: "Unverbindliches Beratungsgespräch"
    },
    {
      icon: Users,
      title: "Erfahrene Experten",
      description: "15+ Jahre Branchenerfahrung"
    }
  ];

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <Badge className="bg-blue-100 text-blue-800">Kontakt & Support</Badge>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Lassen Sie uns über Ihr IT-Projekt sprechen
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Haben Sie Fragen zu unseren Leistungen oder benötigen Sie eine 
            individuelle Beratung? Kontaktieren Sie uns – wir sind für Sie da.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Nachricht senden</CardTitle>
                <CardDescription>
                  Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Ihr vollständiger Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="ihre.email@beispiel.de"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">Unternehmen</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Ihr Unternehmen"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+49 123 456 7890"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service">Interessierte Leistung</Label>
                    <Select onValueChange={handleSelectChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Wählen Sie eine Leistung..." />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      placeholder="Beschreiben Sie Ihr Anliegen oder Projekt..."
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      "Wird gesendet..."
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Nachricht senden
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-8">
            {/* Contact Methods */}
            <Card>
              <CardHeader>
                <CardTitle>Direkter Kontakt</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {contactMethods.map((method, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className={`p-2 rounded-lg ${
                      method.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                      method.color === 'green' ? 'bg-green-100 text-green-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      <method.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-semibold">{method.title}</div>
                      <div className="text-sm text-gray-900">{method.value}</div>
                      <div className="text-xs text-gray-500">{method.description}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Office Hours */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="mr-2 h-5 w-5" />
                  Öffnungszeiten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span>Montag - Freitag</span>
                  <span className="font-semibold">8:00 - 18:00 Uhr</span>
                </div>
                <div className="flex justify-between">
                  <span>Samstag</span>
                  <span className="font-semibold">9:00 - 12:00 Uhr</span>
                </div>
                <div className="flex justify-between">
                  <span>Sonntag</span>
                  <span className="text-gray-500">Geschlossen</span>
                </div>
                <div className="pt-3 border-t">
                  <div className="flex items-center space-x-2">
                    <Headphones className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-semibold text-red-600">
                      IT-Notfall-Support für Kunden
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Location */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="mr-2 h-5 w-5" />
                  Standort
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="font-semibold">NetSyTe GmbH</div>
                  <div className="text-gray-600">
                    {contactInfo.address.street}<br />
                    {contactInfo.address.city}<br />
                    {contactInfo.address.country}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Why Contact Us */}
            <Card className="bg-blue-50">
              <CardHeader>
                <CardTitle>Warum uns kontaktieren?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {reasons.map((reason, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <reason.icon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <div className="font-semibold text-sm">{reason.title}</div>
                      <div className="text-xs text-gray-600">{reason.description}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <section className="mt-20">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-gray-900">
              Häufige Fragen
            </h2>
            <p className="text-lg text-gray-600">
              Antworten auf die wichtigsten Fragen unserer Kunden
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Wie schnell erhalte ich eine Antwort?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Wir melden uns innerhalb von 24 Stunden bei Ihnen zurück. 
                  Bei Notfällen stehen wir Ihnen rund um die Uhr zur Verfügung.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ist die Erstberatung kostenlos?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Ja, die erste Beratung ist für Sie völlig kostenfrei und unverbindlich. 
                  Wir besprechen Ihre Anforderungen und machen Ihnen ein transparentes Angebot.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Bieten Sie auch Remote-Support?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Selbstverständlich! Viele Probleme können wir remote lösen. 
                  Bei komplexeren Aufgaben kommen wir gerne vor Ort.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Welche Regionen bedienen Sie?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Wir sind deutschlandweit für Sie da. Remote-Services bieten wir überall an, 
                  Vor-Ort-Service je nach Region und Verfügbarkeit.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;